import {
  API_ENDPOINTS,
  MANAGE_USER_HTTP_BATCH,
  MANAGE_USER_OP,
  TEAM_MEMBER_ROLE
} from './constants';

/**
 * Build associate ops for Add Team Member modal.
 * @param {string[]} userIds
 * @param {'manager'|'general'} role
 */
export function buildAssociateOperations(userIds = [], role = TEAM_MEMBER_ROLE.MANAGER) {
  const usertype = role === TEAM_MEMBER_ROLE.GENERAL
    ? TEAM_MEMBER_ROLE.GENERAL
    : TEAM_MEMBER_ROLE.MANAGER;
  return (userIds || [])
    .filter(Boolean)
    .map((userid) => ({
      userid,
      usertype,
      type: MANAGE_USER_OP.ASSOCIATE
    }));
}

/**
 * Build a single manageuser operation payload.
 * @param {{ type: string, userid?: string, usertype?: string, fromuserid?: string, touserid?: string }} params
 */
export function buildManageUserOperation({ type, userid, usertype, fromuserid, touserid }) {
  if (type === MANAGE_USER_OP.REPLACE) {
    return {
      type: MANAGE_USER_OP.REPLACE,
      touserid,
      fromuserid,
      usertype
    };
  }
  return { userid, usertype, type };
}

/**
 * All-or-nothing: success → { count }; any failure throws.
 * Rejects legacy partial { succeeded, failed } if failed.length > 0.
 */
export function assertManageUserSuccess(responseData, operations = []) {
  const payload = responseData?.data;

  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const failed = Array.isArray(payload.failed) ? payload.failed : [];
    if (failed.length) {
      const reason = failed[0]?.reason || failed[0]?.message
        || responseData?.message || 'Failed to update team members';
      throw new Error(reason);
    }
  }

  if (responseData?.status === 'success') {
    return { count: (operations || []).length };
  }

  throw new Error(responseData?.message || 'Failed to update team members');
}

function manageUserUrl(cycleId) {
  return `${API_ENDPOINTS.MANAGE_CYCLE_USER}${cycleId}`;
}

/**
 * Single HTTP batch — all-or-nothing.
 */
export async function postManageUserBatch(axios, cycleId, operations) {
  try {
    const response = await axios.post(manageUserUrl(cycleId), { data: operations });
    return assertManageUserSuccess(response?.data, operations);
  } catch (err) {
    if (err?.message && !err?.response) throw err;
    throw new Error(
      err?.response?.data?.message || err?.message || 'Failed to update team members'
    );
  }
}

/**
 * Sequential single-op calls — all-or-nothing (throws on first failure).
 */
export async function postManageUserSequential(axios, cycleId, operations) {
  for (const op of operations) {
    try {
      const response = await axios.post(manageUserUrl(cycleId), { data: [op] });
      assertManageUserSuccess(response?.data, [op]);
    } catch (err) {
      throw new Error(
        err?.response?.data?.message || err?.message || 'Failed to update team members'
      );
    }
  }
  return { count: operations.length };
}

/**
 * Manage cycle users (associate / move / remove / replace).
 * Uses HTTP batch when MANAGE_USER_HTTP_BATCH is true; otherwise sequential.
 * All-or-nothing: returns { count } or throws.
 */
export async function manageCycleUsersRequest(axios, cycleId, operations) {
  if (!cycleId) {
    throw new Error('Cycle id is required');
  }
  if (!Array.isArray(operations) || operations.length === 0) {
    throw new Error('At least one operation is required');
  }

  if (MANAGE_USER_HTTP_BATCH) {
    return postManageUserBatch(axios, cycleId, operations);
  }
  return postManageUserSequential(axios, cycleId, operations);
}
